window.dn = window.dn || {};

/** All methods related to autosuggestion querying */
(function (autosuggest, $, undefined)
{
    /**
     * Used to perform queries to retrieve autosuggestions, or create a new entry. (Which happens
     * is dictated by whatever the endpoint at `opt.url` does, so it's flexible.)
     * 
     * We use JSPath library to facilitate advanced options of `opt.resultPath` and `opt.resultSubs`, which
     * allow you to return a precise subset of data from your API call to the `onSuccess` callback.
     * See: https://github.com/dfilatov/jspath
     * 
     * NOTE: This is used extensively by our `Metadata` defined in the `MessageTemplates` table.
     * 
     * @param {string}      opt.url          - An absolute or relative URL to send an ajax request to, with {{input}} in
     *                                         in the URL where we should put the encoded `opt.input`.
     * @param {string}      opt.method       - The method to use for the web request, defaulting to GET for queries, or POST otherwise.
     * @param {string}      opt.input        - The data typed by the user, either used to search autosuggestions, or if `data` true, to create.
     * @param {string}      opt.data         - If provided, we'll send this through as JSON, replacing any value of "{{input}}" with the value of `opt.input`.
     * @param {string}      opt.resultPath   - (OPTIONAL) If given, this is a JSPath string pointing to a specific part of the results to return. Defaults to ".".
     *                                         E.g. if your results are {"Data": [1, 2, 3]} and you want to return [1, 2, 3], the path is ".Data".
     * @param {string}      opt.resultSubs   - (OPTIONAL) If given, opt.resultPath is required, and this performs substitutions on the resulting data. https://github.com/dfilatov/jspath#substitutions
     * @param {string}      opt.regex        - If set, the `opt.input` must match this regex for us to proceed.
     * @param {function}    opt.onSuccess    - On success, we'll call this function with the returned data.
     * @param {function}    opt.onFailure    - (OPTIONAL) On failure, we'll call this function with null for the 
     *                                         first parameter, and xhr, textStatus, errorThrown for the rest.
     */
    autosuggest.CreateOrQuery = function (opt) 
    {
        if (opt.regex && !opt.regex.test(opt.input))
        {
            // regex was passed in, and did NOT match the `input`
            return;
        }

        var params = 
        {
            type: (opt.method || (opt.data ? "POST" : "GET"))
        };

        if (opt.data)
        {
            // they want to send some JSON data to the endpoint
            params.contentType ="application/json; charset=utf-8";
            params.dataType = "json";

            // if opt.input is an object print to console
            if (typeof opt.input === "object")
            {
                // This is not a simple option creation, it's an advanced version where
                // we're manually passing in an object instead of creating one based on typing.
                params.url = opt.url;

                params.data = JSON.stringify
                (
                    opt.input
                );
            }
            else
            {
                params.url = opt.url.replace("{{input}}", encodeURIComponent(opt.input));

                params.data = JSON.stringify
                (
                    opt.data,

                    function(key, value)
                    {
                        if (value === "{{input}}")
                        {
                            value = opt.input;
                        }

                        return value;
                    }
                );
            }
        }
        else
        {
            params.url = opt.url.replace("{{input}}", encodeURIComponent(opt.input));
        }

        $.ajax(params)
        .done
        (
            function (data) 
            {
                if (opt.resultPath)
                {
                    // Apply the JSPath opt.resultPath to the given data to return a subset of data.
                    opt.onSuccess(JSPath.apply(opt.resultPath, data, opt.resultSubs));
                }
                else
                {
                    // Synonymous with a JSPath of ".", returning the top-level data.
                    opt.onSuccess(data);
                }
            }
        )
        .fail
        (
            function (xhr, textStatus, errorThrown) 
            {
                if (opt.onFailure)
                {
                    opt.onFailure(null, xhr, textStatus, errorThrown);
                }
            }
        );
    };

    /**
     * Setup an input field to do autosuggestions for DN users.
     * 
     * Returns the selectize element that is created. 
     * 
     * @param {string}  opt.selector        - The jQuery selector of the `input` element(s) to wire up for user autosuggest.
     * @param {array}   opt.prefilledUsers  - A list of UserKeys and/or email addresses of users that will be preset in the form.
     * @param {array}   opt.suggestedUsers  - An array of User objects for initial user suggestions, if the user clicks into the user field, they can choose from these without typing.
     * @param {bool}    opt.suggestExisting - If true, we'll search our users for autosuggestions. Otherwise, just email addresses are accepted.
     * @param {func}    opt.onChange        - Called when a change is made in the user autosuggestion field, and after initial setup.
     */
    autosuggest.UserSetup = function(opt)
    {
        // If not set, set it so we don't need to check if it's set every time.
        opt.onChange = opt.onChange || function() { };

        // AUTOSUGGESTIONS for Users
        var selectizedElement = $(opt.selector).selectize
        (
            {
                loadThrottle: dn.constants.WaitForAutosuggest,
                valueField: "UserKey",
                searchField: ["Name", "BusinessCity", "BusinessLocation","BusinessName"],
                // These next two lines pre-fill any existing users
                options: (opt.suggestedUsers || []),
                items: opt.prefilledUsers,
                sortField: 
                [
                    {
                        field: 'Name',
                        direction: 'asc'
                    },
                    {
                        field: 'BusinessCity',
                        direction: 'asc'
                    },
                    {
                        field: 'BusinessLocation',
                        direction: 'asc'
                    },
                    {
                        field: 'BusinessName',
                        direction: 'asc'
                    },
                    {
                        field: '$score'
                    }
                ],
                persist: true,
                hideSelected: true,
                selectOnTab: true,
                plugins: opt.plugins ? opt.plugins : ['remove_button', 'restore_on_backspace'],
                mode: 'multi',
                highlight: false,
                maxItems: opt.maxItems,
                openOnFocus: false,
                closeAfterSelect: true,
                preload: false,
                create: true,
                splitOn: /[\r\n]+|[\s,;:"“”'’]+/,
                createFilter: function (query) 
                {
                    // only allow them to add email addresses
                    return is.email(query); 
                },
                onItemAdd: function (identifier, element) 
                {
                    opt.onChange();
                },
                onItemRemove: function (identifier) 
                {
                    opt.onChange();
                },
                load: function (input, callback) 
                {
                    if (opt.suggestExisting && !is.email(input))
                    {
                        // Don't bother sending the API query unless suggestExisting is true, 
                        // and this is NOT an email address.
                        autosuggest.CreateOrQuery
                        (
                            {
                                url: "/api/v1/user/query/{{input}}", 
                                input: input, 
                                regex: /^.+$/,          // the user must have typed something
                                onSuccess: callback,
                                onFailure: callback
                            }
                        );
                    }
                },
                render: 
                {
                    option: function (data, escape) 
                    {
                        if (is.email(data.UserKey))
                        {
                            // Handle manually-entered email addresses (if removed, they still appear in the options list)
                            return '<div class="dn-user-choice"><span class="dn-user-email">' + escape(data.UserKey) + '</span></div>';
                        }
                        else
                        {
                            return '<div class="dn-user-choice"><a href="' + data.Link + '" target="' + dn.constants.LinkTargetNewWindow + '"><img class="dn-user-avatar" src="' + data.UrlProfilePhoto + '" /></a><span class="dn-user-name">' + escape(data.Name) + '</span><span class="text-small text-muted float-right">' + escape(data.BusinessCity) + ', ' + escape(data.BusinessLocation) + '</span></div>';
                        }
                    },
                    item: function (data, escape) 
                    {
                        if (is.email(data.UserKey))
                        {
                            // Handle manually-entered email addresses
                            return '<div class="dn-user">' + escape(data.UserKey) + '</div>';
                        }
                        else
                        {
                            return '<div class="dn-user"><a href="' + data.Link + '" target="' + dn.constants.LinkTargetNewWindow + '"><img class="dn-user-avatar" src="' + data.UrlProfilePhoto + '" />' + escape(data.Name) + '</a></div>';
                        }
                    }
                }
            }
        )[0].selectize;

        opt.onChange();

        return selectizedElement;
    };

}(window.dn.autosuggest = window.dn.autosuggest || {}, jQuery));
